import type { ComponentProps, ReactNode } from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { msg } from '@gonfalon/messages/msg';
import { Button, Popover } from '@launchpad-ui/components';
import { Icon, type IconName } from '@launchpad-ui/icons';

import type { Operator, ParsedToken } from '../../../types/QueryBuilder';
import { useQueryBuilderContext } from '../context';
import { removeToken, replaceToken, stringifyToken } from '../utils';

import { FilterValueAutocomplete } from './FilterValueAutocomplete';
import type { FilterValueOption } from './FilterValueAutocomplete';

import styles from './FieldValueSearch.module.css';

export interface FieldValueSearchToken {
  fieldKey: string;
  operator?: Operator;
  values?: Array<string | number | boolean>;
}

export interface FieldValueSearchProps extends Omit<
  ComponentProps<typeof Button>,
  'children' | 'value' | 'onChange' | 'onPress'
> {
  token: FieldValueSearchToken;
  options?: FilterValueOption[];
  placeholder?: string;
  label?: string;
  iconName?: IconName;
  multiSelect?: boolean;
  renderOption?: (option: FilterValueOption) => ReactNode;
  /** Label for a leading option that clears the field, e.g. "All context kinds". */
  clearOptionLabel?: string;
  /** Post-processes the query this control writes, e.g. to drop filters scoped to the previous value. */
  transformQuery?: (nextQuery: string) => string;
}

/** ListBox id for the leading clear row; single-select can't otherwise deselect. */
export const CLEAR_VALUE_SENTINEL = '__filter_value_clear__';

const VALUE_MAX_LEN = 16;

function truncateValue(value: string): string {
  return value.length > VALUE_MAX_LEN ? `${value.slice(0, VALUE_MAX_LEN - 1)}…` : value;
}

/** Primary trigger text plus an optional count suffix that should not ellipsize. */
export function buildButtonLabelParts(
  selectedValues: Array<string | number | boolean>,
  placeholder: string,
  label?: string,
  { truncateSingleValue = true }: { truncateSingleValue?: boolean } = {},
): { primary: string; overflow?: string } {
  if (selectedValues.length === 0) {
    return { primary: placeholder };
  }
  const firstValue = String(selectedValues[0]);
  const first = selectedValues.length === 1 && !truncateSingleValue ? firstValue : truncateValue(firstValue);
  const primary = label ? `${label}: ${first}` : first;
  if (selectedValues.length === 1) {
    return { primary };
  }
  return { primary, overflow: `+${selectedValues.length - 1}` };
}

export function buildButtonLabel(
  selectedValues: Array<string | number | boolean>,
  placeholder: string,
  label?: string,
): string {
  const { primary, overflow } = buildButtonLabelParts(selectedValues, placeholder, label);
  return overflow ? `${primary} ${overflow}` : primary;
}

export function FieldValueSearch({
  token,
  options = [],
  placeholder = msg('queryBuilder.filterValues.selectValues'),
  label,
  className,
  isDisabled,
  iconName,
  multiSelect = true,
  renderOption,
  clearOptionLabel,
  transformQuery,
  ...buttonProps
}: FieldValueSearchProps) {
  const { query, parsedQuery, dispatch, registerFieldOwner } = useQueryBuilderContext();
  const { fieldKey, operator = '=', values } = token;
  const selectedValues = values ?? [];

  const buttonRef = useRef<HTMLButtonElement>(null);
  const listboxId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const lastCloseAtRef = useRef(0);

  useEffect(
    () => registerFieldOwner(fieldKey, { focus: () => buttonRef.current?.focus() }),
    [fieldKey, registerFieldOwner],
  );

  function writeValues(nextValues: Array<string | number | boolean>) {
    const existing = parsedQuery.find(
      (t): t is Extract<ParsedToken, { type: 'FILTER' }> => t.type === 'FILTER' && t.field === fieldKey,
    );

    let newQuery: string;
    if (nextValues.length === 0) {
      newQuery = existing ? removeToken(query, existing) : query;
    } else {
      const replacement = stringifyToken({
        type: 'FILTER',
        field: fieldKey,
        operator,
        value: nextValues,
        text: '',
        location: { start: { offset: 0 }, end: { offset: 0 } },
      });
      if (existing) {
        newQuery = replaceToken(query, existing, replacement);
      } else {
        newQuery = query ? `${query} ${replacement}` : replacement;
      }
    }

    dispatch({ type: 'SET_QUERY', query: transformQuery ? transformQuery(newQuery) : newQuery });
  }

  function handleToggle(value: string | number | boolean) {
    const key = String(value);
    const exists = selectedValues.some((v) => String(v) === key);
    const nextValues = exists ? selectedValues.filter((v) => String(v) !== key) : [...selectedValues, value];
    writeValues(nextValues);
  }

  function handleSelect(value: string | number | boolean) {
    if (clearOptionLabel !== undefined && value === CLEAR_VALUE_SENTINEL) {
      writeValues([]);
      setIsOpen(false);
      return;
    }
    if (multiSelect) {
      handleToggle(value);
      return;
    }
    writeValues([value]);
    setIsOpen(false);
  }

  function handleOpenChange(open: boolean) {
    setIsOpen(open);
    if (!open) {
      lastCloseAtRef.current = Date.now();
      setSearchText('');
    }
  }

  function handlePress() {
    if (Date.now() - lastCloseAtRef.current < 150) {
      return;
    }
    setIsOpen((open) => !open);
  }

  // Show what the option list shows, not the raw stored value (e.g. a kind's name, not its key).
  const displayValues = selectedValues.map((value) => {
    const option = options.find((candidate) => String(candidate.value) === String(value));
    return option?.displayName ?? value;
  });
  const buttonLabel = buildButtonLabel(displayValues, placeholder, label);

  return (
    <>
      <Button
        ref={buttonRef}
        className={className}
        isDisabled={isDisabled}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        onPress={handlePress}
        {...buttonProps}
      >
        {iconName && <Icon name={iconName} size="small" aria-hidden />}
        <span>{buttonLabel}</span>
        <Icon name="chevron-down" size="small" />
      </Button>
      <Popover
        triggerRef={buttonRef}
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        placement="bottom start"
        className={styles.popover}
        aria-label={msg('queryBuilder.filterValues.popoverLabel')}
      >
        <FilterValueAutocomplete
          id={listboxId}
          multiSelect={multiSelect}
          options={
            clearOptionLabel === undefined
              ? options
              : [{ value: CLEAR_VALUE_SENTINEL, displayName: clearOptionLabel }, ...options]
          }
          inputValue={searchText}
          onInputChange={setSearchText}
          selectedValues={selectedValues}
          onSelect={handleSelect}
          renderOption={renderOption}
          autoFocus
          placeholder={
            label
              ? msg('queryBuilder.filterValues.searchPlaceholder', { field: label.toLowerCase() })
              : msg('queryBuilder.filterValues.searchValuesPlaceholder')
          }
          onDismiss={() => handleOpenChange(false)}
        />
      </Popover>
    </>
  );
}
